import {Injectable} from '@angular/core';
import {Observable, of, throwError, map} from 'rxjs';
import {FixedAssetsService} from './fixed-assets.service';
import {FixedAssetConfigModel} from '../DataModels/fixed-asset-config-model';
import {FixedAssetModel} from '../DataModels/fixed-asset-model';
import {BsToastService} from './bs-toast-service';
import {DepreciationMethod, ProrateConvention} from '../DataModels/depreciation-models';
import {FixedAssetGlPostingPayload, GlDetailLine} from '../DataModels/fixed-asset-posting-payload';
import {DataService} from './data.service';
import {AuthService} from './auth.service';

@Injectable({providedIn: 'root'})
export class FixedAssetsPostingService {
  constructor(
    private assets: FixedAssetsService,
    private notify: BsToastService,
    private api: DataService,
    private auth: AuthService,
  ) {
  }

  /** Format a date as YYYY-MM */
  private ym(d: Date): string {
    const y = d.getFullYear();
    const m = (d.getMonth() + 1).toString().padStart(2, '0');
    return `${y}-${m}`;
  }

  /** Build inclusive range of months between start (inService) and end (today) in YYYY-MM keys */
  private monthsBetween(start: Date, end: Date): string[] {
    const s = new Date(start.getFullYear(), start.getMonth(), 1);
    const e = new Date(end.getFullYear(), end.getMonth(), 1);
    const out: string[] = [];
    for (let d = new Date(s); d <= e; d.setMonth(d.getMonth() + 1)) {
      out.push(this.ym(d));
    }
    return out;
  }

  /** Get the end date of the period that contains a given year-month according to convention */
  private periodEndFor(year: number, monthIndex0: number, conv: ProrateConvention): Date {
    // monthIndex0: 0..11
    if (conv === ProrateConvention.Monthly) {
      // last day of month
      return new Date(year, monthIndex0 + 1, 0);
    }
    if (conv === ProrateConvention.Quarterly) {
      const q = Math.floor(monthIndex0 / 3); // 0..3
      const endMonth = q * 3 + 2; // 2,5,8,11
      return new Date(year, endMonth + 1, 0);
    }
    if (conv === ProrateConvention.BiAnnual) {
      const isH1 = monthIndex0 < 6;
      const endMonth = isH1 ? 5 : 11; // Jun or Dec
      return new Date(year, endMonth + 1, 0);
    }
    // Annual
    return new Date(year, 12, 0); // Dec 31
  }

  /** Filter unposted month keys down to those that belong to fully completed periods */
  private filterToCompletedPeriods(keys: string[], asOf: Date, conv: ProrateConvention): string[] {
    const completed: string[] = [];
    for (const key of keys) {
      const [yStr, mStr] = key.split('-');
      const y = parseInt(yStr, 10);
      const m0 = parseInt(mStr, 10) - 1;
      const periodEnd = this.periodEndFor(y, m0, conv);
      if (periodEnd < new Date(asOf.getFullYear(), asOf.getMonth(), asOf.getDate())) {
        completed.push(key);
      }
    }
    return completed;
  }

  /** Sum depreciation for the given months using the asset.schedule periodStart */
  private sumDepForMonths(asset: FixedAssetModel, monthKeys: string[]): number {
    const set = new Set(monthKeys);
    const schedule = asset.schedule || [];
    let total = 0;
    for (const entry of schedule) {
      const ps = (entry as any).periodStart instanceof Date ? (entry as any).periodStart as Date : new Date((entry as any).periodStart);
      if (set.has(this.ym(ps))) {
        total += Number((entry as any).depreciationAmount) || 0;
      }
    }
    return +total.toFixed(2);
  }

  /** Resolve accounts with precedence: asset-specific then config */
  private resolveAccounts(asset: FixedAssetModel, cfg: FixedAssetConfigModel) {
    const expense = asset.depreciationExpenseAccountId || cfg.defaultDepreciationExpenseAccountId || '';
    const accum = asset.accumulatedDepreciationAccountId || cfg.defaultAccumulatedDepreciationAccountId || '';
    return {expense, accum};
  }

  /** Build GL posting payload for depreciation using the new snake_case shape */
  buildDepPostingPayload(assets: FixedAssetModel[], cfg: FixedAssetConfigModel, today: Date = new Date()): FixedAssetGlPostingPayload | null {
    if (!assets || assets.length === 0) {
      return null;
    }

    const conv: ProrateConvention = (cfg as any)?.prorateConvention || ProrateConvention.Monthly;
    const assetUpdates: Array<{ assetId: string; postedMonths: string[]; lastPostedDepDate: string }> = [];
    // For expenses, we now produce one line per asset (not aggregated)
    // Keep aggregation by account only for accumulated depreciation (credit) lines
    const perAssetExpense: Array<{
      account_id: string;
      amount: number;
      asset_id: string;
      asset_tag?: string;
    }> = [];
    // Aggregate amounts by account for accumulated (credit)
    const accumByAcct = new Map<string, number>();
    let total = 0;
    const skipped: string[] = [];

    // Track the latest (max) period-end date across all assets to use as the journal header_date
    let maxPeriodEnd: Date | null = null;

    for (const asset of assets) {
      if (!asset) {
        continue;
      }
      if (asset.depreciationMethod === DepreciationMethod.None) {
        skipped.push(`[${asset.id}] ${asset.name || ''} — depreciation method is None`);
        continue;
      }
      // Skip disposed assets (no further depreciation postings)
      if ((asset as any)?.disposal?.disposalDate) {
        skipped.push(`[${asset.id}] ${asset.name || ''} — disposed; depreciation not allowed`);
        continue;
      }
      const inSvcStr = asset.inServiceDate || asset.acquisitionDate;
      if (!inSvcStr) {
        skipped.push(`[${asset.id}] ${asset.name || ''} — missing in-service/acquisition date`);
        continue;
      }
      const {expense, accum} = this.resolveAccounts(asset, cfg as any);
      if (!expense || !accum) {
        skipped.push(`[${asset.id}] ${asset.name || ''} — missing required GL accounts (expense/accum)`);
        continue;
      }

      const inSvc = new Date(inSvcStr);
      const monthsAll = this.monthsBetween(inSvc, today);
      const already = new Set(asset.postedDepMonths || []);
      const unposted = monthsAll.filter(k => !already.has(k));
      const toPost = this.filterToCompletedPeriods(unposted, today, conv);
      if (!toPost.length) {
        skipped.push(`[${asset.id}] ${asset.name || ''} — no completed, unposted periods as of today`);
        continue;
      }

      const amount = this.sumDepForMonths(asset, toPost);
      if (amount <= 0) {
        skipped.push(`[${asset.id}] ${asset.name || ''} — computed amount <= 0`);
        continue;
      }

      // Record one expense line per asset (debit)
      perAssetExpense.push({
        account_id: String(expense),
        amount: +Number(amount).toFixed(2),
        asset_id: String(asset.id),
        asset_tag: (asset.assetTag as any) || undefined,
      });
      // Aggregate accumulated depreciation amount by accum account
      accumByAcct.set(accum, +(Number(accumByAcct.get(accum) || 0) + Number(amount)).toFixed(2));
      total += amount;

      // Determine last posted date for this asset
      const lastKey = toPost[toPost.length - 1];
      const [yStr, mStr] = lastKey.split('-');
      const y = parseInt(yStr, 10);
      const m0 = parseInt(mStr, 10) - 1;
      const lastPeriodEnd = this.periodEndFor(y, m0, conv);
      // Track max period end across assets
      if (!maxPeriodEnd || lastPeriodEnd > maxPeriodEnd) {
        maxPeriodEnd = new Date(lastPeriodEnd.getFullYear(), lastPeriodEnd.getMonth(), lastPeriodEnd.getDate());
      }
      const isoDate = `${lastPeriodEnd.getFullYear()}-${String(lastPeriodEnd.getMonth() + 1).padStart(2, '0')}-${String(lastPeriodEnd.getDate()).padStart(2, '0')}`;
      assetUpdates.push({assetId: asset.id, postedMonths: toPost, lastPostedDepDate: isoDate});
    }

    if (skipped.length) {
      try {
        console.log('[FA Dep Posting] Skipped assets:', skipped);
      } catch {
      }
    }
    if (total <= 0) {
      this.notify.showInfoToast($localize`:Nothing to post@@nothingToPost:No unposted depreciation for current period(s).`);
      return null;
    }
    // Determine header date as the max period-end found; fallback to today's date if none
    const headerBaseDate = maxPeriodEnd || today;
    const yyyy = headerBaseDate.getFullYear();
    const mm = String(headerBaseDate.getMonth() + 1).padStart(2, '0');
    const dd = String(headerBaseDate.getDate()).padStart(2, '0');
    // Resolve company context from the assets (all assets in a batch are expected to belong to the same company)
    const companyId = (assets.find(a => !!(a as any)?.companyId) as any)?.companyId || undefined;
    const locationId = (assets.find(a => !!(a as any)?.locationId) as any)?.locationId || undefined;
    const entityId = (assets.find(a => !!(a as any)?.entityId) as any)?.entityId || undefined;

    // Enforce required company_id per spec
    if (!companyId) {
      this.notify.showWarningToast($localize`:MissingCompanyId@@missingCompanyId:Company is required to post depreciation.`);
      return null;
    }

    // Build detail lines: per-asset expense debits; aggregated accumulated credits
    const detail: GlDetailLine[] = [];
    // Human-readable month/year for descriptions
    const monthLabel = headerBaseDate.toLocaleString(undefined, {month: 'long', year: 'numeric'});
    // One expense line per asset; debit_credit must equal the amount value but signed for DR/CR
    perAssetExpense.forEach((x) => {
      const tagPart = x.asset_tag ? ` (Tag: ${String(x.asset_tag)})` : '';
      const amt = +Number(x.amount).toFixed(2);
      detail.push({
        id: '00000000-0000-0000-0000-000000000000',
        account_id: x.account_id,
        amount: amt,
        // debit (positive): debit_credit equals the positive amount
        debit_credit: amt as any,
        header_id: '00000000-0000-0000-0000-000000000000',
        quantity: 1,
        control_line: false,
        description: `${$localize`:DepExpense@@depExpense:Depreciation Expense`} - ${monthLabel}${tagPart}`,
        asset_id: x.asset_id,
      } as any);
    });
    accumByAcct.forEach((amt, acct) => {
      const posAmt = +Number(amt).toFixed(2);
      detail.push({
        id: '00000000-0000-0000-0000-000000000000',
        account_id: acct,
        amount: posAmt,
        // For credits, debit_credit equals the negative amount
        debit_credit: (-1 * posAmt) as any,
        header_id: '00000000-0000-0000-0000-000000000000',
        quantity: 1,
        control_line: false,
        description: `${$localize`:AccumDep@@accumDep:Accumulated Depreciation`} - ${monthLabel}`,
      });
    });

    const monthNum = String(headerBaseDate.getMonth() + 1).padStart(2, '0');
    const header_date = `${yyyy}-${mm}-${dd}T00:00:00`;
    // Resolve current user id for required user_id
    let user_id = '';
    try {
      const u = this.auth.currentUser();
      user_id = String((u && (u.id || (u.userId ?? u.username))) || '');
    } catch {
      user_id = '';
    }
    const batchDescMonth = headerBaseDate.toLocaleString(undefined, {month: 'long', year: 'numeric'});
    // Build tag suffix for header description (single asset: Tag; multi: Tags)
    const tags = Array.from(new Set((assets || []).map(a => String((a as any)?.assetTag || '').trim()).filter(t => !!t)));
    const tagSuffix = tags.length ? (tags.length === 1 ? ` - Tag: ${tags[0]}` : ` - Tags: ${tags.join(', ')}`) : '';

    const payload: FixedAssetGlPostingPayload = {
      JournalEntry: {
        batch: {
          id: '00000000-0000-0000-0000-000000000000',
          type_id: 1,
          description: `Monthly Depreciation Batch - ${batchDescMonth}`,
          user_id,
        },
        header: {
          id: '00000000-0000-0000-0000-000000000000',
          batch_id: '00000000-0000-0000-0000-000000000000',
          company_id: companyId || '',
          location_id: locationId,
          entity_id: entityId,
          header_date,
          description: `Monthly Depreciation Posting - ${headerBaseDate.toLocaleString(undefined, {month: 'short'})} ${yyyy}${tagSuffix}`,
          number: `DEP-${yyyy}-${monthNum}`,
        },
        detail,
      },
      AssetUpdates: assetUpdates.map(u => ({
        AssetId: u.assetId,
        PostedMonths: u.postedMonths,
        LastPostedDepDate: `${u.lastPostedDepDate}T00:00:00`,
      })),
    };
    return payload;
  }

  /** Build payload then invoke backend to post as a batch */
  postDepreciationBatch(assets: FixedAssetModel[], cfg: FixedAssetConfigModel, today: Date = new Date()): Observable<{
    entryId: string;
    count: number;
    amount: number
  }> {
    // New intent-based API: plural AssetIds + AsOf (commit only)
    if (!assets || assets.length === 0) {
      return of({entryId: '', count: 0, amount: 0});
    }

    // Compute AsOf as the end of the latest completed unposted period for the first asset
    const primary = assets[0];
    const asOf = this.computeAsOfForDep(primary, cfg, today);
    if (!asOf) {
      this.notify.showInfoToast($localize`:Nothing to post@@nothingToPost:No unposted depreciation for current period(s).`);
      return of({entryId: '', count: 0, amount: 0});
    }

    // Resolve org/user context from the first asset (batch currently single-asset from form; supports multi later)
    const companyId = (primary as any)?.companyId || '';
    const locationId = (primary as any)?.locationId || undefined;
    const entityId = (primary as any)?.entityId || undefined;
    if (!companyId) {
      this.notify.showWarningToast($localize`:MissingCompanyId@@missingCompanyId:Company is required to post depreciation.`);
      return of({entryId: '', count: 0, amount: 0});
    }
    let userId = '';
    try {
      const u = this.auth.currentUser();
      userId = String((u && (u.id || (u.userId ?? u.username))) || '');
    } catch {
    }

    const intent: any = {
      AssetIds: assets.map(a => String(a.id)),
      AsOf: this.toIsoMidnight(asOf),
      CompanyId: companyId,
      LocationId: locationId,
      EntityId: entityId,
      UserId: userId || undefined,
    };
    // Log exact intent
    try {
      console.log('[FixedAssetsPostingService][postDepreciationBatch] intent -> fixed-assets/posting/depreciation', intent);
    } catch {
    }

    return this.api.post('fixed-assets/posting/depreciation', intent).pipe(
      map((res: any) => {
        try {
          console.log('[FixedAssetsPostingService][postDepreciationBatch] API response from fixed-assets/posting/depreciation', res);
        } catch {
        }
        return res;
      }),
      map((res: any) => {
        const posted = Number(res?.AssetsPosted ?? res?.assetsPosted ?? res?.posted ?? res?.count ?? 1);
        const amount = Number(res?.TotalAmount ?? res?.totalAmount ?? 0);
        const entryId = String(res?.entryId || res?.id || res?.DepreciationEntry?.header?.number || '');
        return {entryId, count: posted, amount};
      })
    );
  }

  /** Compute AsOfDate for depreciation posting based on completed periods and unposted months */
  private computeAsOfForDep(asset: FixedAssetModel, cfg: FixedAssetConfigModel, today: Date): Date | null {
    try {
      if (!asset) {
        return null;
      }
      const inSvcStr = asset.inServiceDate || asset.acquisitionDate;
      if (!inSvcStr) {
        return null;
      }
      const conv: ProrateConvention = (cfg as any)?.prorateConvention || ProrateConvention.Monthly;
      const inSvc = new Date(inSvcStr);
      const monthsAll = this.monthsBetween(inSvc, today);
      const already = new Set(asset.postedDepMonths || []);
      const unposted = monthsAll.filter(k => !already.has(k));
      const completed = this.filterToCompletedPeriods(unposted, today, conv);
      if (!completed.length) {
        return null;
      }
      // choose the end date of the last completed period key
      const lastKey = completed[completed.length - 1];
      const [yStr, mStr] = lastKey.split('-');
      const y = parseInt(yStr, 10);
      const m0 = parseInt(mStr, 10) - 1;
      const pe = this.periodEndFor(y, m0, conv);
      return new Date(pe.getFullYear(), pe.getMonth(), pe.getDate());
    } catch {
      return null;
    }
  }

  /** Local helper: normalize Date to ISO with midnight */
  private toIsoMidnight(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}T00:00:00`;
  }

  /** Build disposal posting payload with GAAP-compliant detail lines and optional depreciation catch-up */
  private buildDisposalPayload(asset: FixedAssetModel, cfg: FixedAssetConfigModel, disposal: {
    date: string;
    amount: number;
    expenses?: number
  }): any {
    const amount = +(Number(disposal.amount || 0).toFixed(2));
    const expenses = disposal.expenses != null ? +Number(disposal.expenses).toFixed(2) : undefined;
    const d = new Date(disposal.date);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const header_date = `${yyyy}-${mm}-${dd}T00:00:00`;

    // Resolve org/account context if available
    const companyId = (asset as any)?.companyId || '';
    const locationId = (asset as any)?.locationId;
    const entityId = (asset as any)?.entityId;
    // Resolve current user id for required user_id
    let user_id = '';
    try {
      const u = this.auth.currentUser();
      user_id = String((u && (u.id || (u.userId ?? u.username))) || '');
    } catch {
      user_id = '';
    }

    // Helper: sum depreciation from schedule up to a cutoff with proration for the cutoff period
    const computeAccumThrough = (cutoff: Date): number => {
      const sched = (asset as any)?.schedule || [];
      let total = 0;
      for (const e of sched) {
        const ps = e.periodStart instanceof Date ? e.periodStart : new Date(e.periodStart);
        const pe = e.periodEnd instanceof Date ? e.periodEnd : new Date(e.periodEnd);
        const amt = Number(e.depreciationAmount || 0);
        if (!isFinite(amt)) {
          continue;
        }
        if (pe < cutoff) {
          total += amt;
        } else if (ps <= cutoff && cutoff <= pe) {
          // prorate within the period by days used
          const daysInPeriod = Math.max(1, Math.floor((pe.getTime() - ps.getTime()) / 86400000) + 1);
          const daysUsed = Math.max(0, Math.min(daysInPeriod, Math.floor((cutoff.getTime() - ps.getTime()) / 86400000) + 1));
          const proration = Math.max(0, Math.min(1, daysUsed / daysInPeriod));
          total += +(amt * proration).toFixed(2);
        }
      }
      return +total.toFixed(2);
    };

    // Helper: sum of posted depreciation based on posted months (assumes monthly schedule periods)
    const computePostedSum = (): number => {
      const months = new Set<string>((asset as any)?.postedDepMonths || []);
      if (!months.size) {
        return 0;
      }
      const sched = (asset as any)?.schedule || [];
      let total = 0;
      for (const e of sched) {
        const ps = e.periodStart instanceof Date ? e.periodStart : new Date(e.periodStart);
        const key = this.ym(ps);
        if (months.has(key)) {
          total += Number(e.depreciationAmount || 0) || 0;
        }
      }
      return +total.toFixed(2);
    };

    const accumulatedThrough = computeAccumThrough(d);
    const postedSum = computePostedSum();
    const catchUp = Math.max(0, +(accumulatedThrough - postedSum).toFixed(2));

    const acquisitionCost = Number((asset as any)?.acquisitionCost || 0);
    const nbv = +(acquisitionCost - accumulatedThrough).toFixed(2);
    const netProceeds = +(amount - (expenses || 0)).toFixed(2);
    const gainLoss = +(netProceeds - nbv).toFixed(2);

    // Resolve accounts from asset or config
    const assetAcct = (asset as any)?.assetAccountId || (cfg as any)?.defaultAssetAccountId || '';
    const accumAcct = (asset as any)?.accumulatedDepreciationAccountId || (cfg as any)?.defaultAccumulatedDepreciationAccountId || '';
    const depExpAcct = (asset as any)?.depreciationExpenseAccountId || (cfg as any)?.defaultDepreciationExpenseAccountId || '';
    const gainAcct = (asset as any)?.gainOnDisposalAccountId || (cfg as any)?.defaultGainOnDisposalAccountId || '';
    const lossAcct = (asset as any)?.lossOnDisposalAccountId || (cfg as any)?.defaultLossOnDisposalAccountId || '';
    // Proceeds Suspense account: prefer asset-specific, fallback to config default
    const proceedsAcct =
      (asset as any)?.proceedsSuspenseAccountId ||
      (cfg as any)?.defaultProceedsSuspenseAccountId ||
      'PROCEEDS-ACCOUNT-PENDING';

    // Build DepreciationEntry (only if catch-up > 0.00)
    const depreciationDetail: any[] = [];
    if (catchUp > 0.0001) {
      depreciationDetail.push({
        id: '00000000-0000-0000-0000-000000000000',
        header_id: '00000000-0000-0000-0000-000000000000',
        account_id: String(depExpAcct),
        amount: catchUp,
        // debit_credit must equal the amount value for debits
        debit_credit: catchUp,
        quantity: 1,
        control_line: false,
        description: 'Final Depreciation to Disposal'
      });
      depreciationDetail.push({
        id: '00000000-0000-0000-0000-000000000000',
        header_id: '00000000-0000-0000-0000-000000000000',
        account_id: String(accumAcct),
        amount: catchUp,
        // credit: debit_credit equals negative amount
        debit_credit: -catchUp,
        quantity: 1,
        control_line: false,
        description: 'Final Depreciation to Disposal'
      });
    }

    // Build DisposalEntry lines
    const disposalDetail: any[] = [];
    // Net proceeds line handling:
    // - If netProceeds > 0: DR Proceeds Suspense for the net amount.
    // - If netProceeds < 0: CR Proceeds Suspense for abs(netProceeds) (e.g., expenses exceed proceeds).
    // - If netProceeds == 0: omit the Proceeds Suspense line.
    if (netProceeds > 0.0001) {
      disposalDetail.push({
        id: '00000000-0000-0000-0000-000000000000',
        header_id: '00000000-0000-0000-0000-000000000000',
        account_id: String(proceedsAcct),
        amount: netProceeds,
        debit_credit: netProceeds, // DR
        quantity: 1,
        control_line: false,
        description: 'Net Proceeds'
      });
    } else if (netProceeds < -0.0001) {
      const absNet = Math.abs(netProceeds);
      disposalDetail.push({
        id: '00000000-0000-0000-0000-000000000000',
        header_id: '00000000-0000-0000-0000-000000000000',
        account_id: String(proceedsAcct),
        amount: absNet,
        debit_credit: -absNet, // CR
        quantity: 1,
        control_line: false,
        description: 'Net Proceeds (negative)'
      });
    }
    // Remove asset cost
    disposalDetail.push({
      id: '00000000-0000-0000-0000-000000000000',
      header_id: '00000000-0000-0000-0000-000000000000',
      account_id: String(assetAcct),
      amount: Math.abs(acquisitionCost),
      // credit: negative amount in debit_credit
      debit_credit: -Math.abs(acquisitionCost),
      quantity: 1,
      control_line: false,
      description: 'Remove Asset Cost'
    });
    // Remove accumulated depreciation
    disposalDetail.push({
      id: '00000000-0000-0000-0000-000000000000',
      header_id: '00000000-0000-0000-0000-000000000000',
      account_id: String(accumAcct),
      amount: Math.abs(accumulatedThrough),
      // debit: positive amount in debit_credit
      debit_credit: Math.abs(accumulatedThrough),
      quantity: 1,
      control_line: false,
      description: 'Remove Accumulated Depreciation'
    });
    // Gain or loss line (expenses already netted in netProceeds)
    if (gainLoss > 0.0001) {
      // gain (credit)
      disposalDetail.push({
        id: '00000000-0000-0000-0000-000000000000',
        header_id: '00000000-0000-0000-0000-000000000000',
        account_id: String(gainAcct),
        amount: gainLoss,
        // credit: negative amount in debit_credit
        debit_credit: -gainLoss,
        quantity: 1,
        control_line: false,
        description: 'Gain on Disposal'
      });
    } else if (gainLoss < -0.0001) {
      // loss (debit)
      disposalDetail.push({
        id: '00000000-0000-0000-0000-000000000000',
        header_id: '00000000-0000-0000-0000-000000000000',
        account_id: String(lossAcct),
        amount: Math.abs(gainLoss),
        // debit: positive amount in debit_credit
        debit_credit: Math.abs(gainLoss),
        quantity: 1,
        control_line: false,
        description: 'Loss on Disposal'
      });
    }

    // Build payload per new shape
    const tag = String((asset as any)?.assetTag || '').trim();
    const tagPart = tag ? ` (Tag: ${tag})` : '';

    const payload: any = {
      AssetId: String((asset as any)?.id ?? ''),
      DisposalDate: header_date,
      Amount: amount,
      Expenses: expenses,
      DisposalEntry: {
        batch: {
          id: '00000000-0000-0000-0000-000000000000',
          type_id: 1,
          description: `Asset Disposal - ${String((asset as any)?.name || (asset as any)?.id || '')}`,
          user_id,
        },
        header: {
          id: '00000000-0000-0000-0000-000000000000',
          batch_id: '00000000-0000-0000-0000-000000000000',
          company_id: companyId,
          location_id: locationId,
          entity_id: entityId,
          header_date,
          description: `Disposal of ${(asset as any)?.name || (asset as any)?.id || ''}${tagPart}`,
          number: `DISP-${yyyy}-${mm}`,
        },
        detail: disposalDetail,
      },
      DepreciationEntry: {
        batch: {
          id: '00000000-0000-0000-0000-000000000000',
          type_id: 1,
          description: 'Final Depreciation to Disposal',
          user_id,
        },
        header: {
          id: '00000000-0000-0000-0000-000000000000',
          batch_id: '00000000-0000-0000-0000-000000000000',
          company_id: companyId,
          location_id: locationId,
          entity_id: entityId,
          header_date,
          description: `Final Depreciation - ${d.toLocaleString(undefined, {month: 'short'})} ${yyyy}${tagPart}`,
          number: `DEP-FINAL-${yyyy}-${mm}`,
        },
        detail: depreciationDetail,
      },
      // For forward compatibility: include months posted if backend accepts it later
      AssetUpdates: [
        {
          AssetId: String((asset as any)?.id ?? ''),
          PostedMonths: [],
          LastPostedDepDate: header_date,
          DisposalDate: header_date,
          Proceeds: amount,
          Expenses: expenses,
        }
      ]
    };

    // Sanity: log balances to help verify double-entry
    try {
      // When debit_credit equals the signed amount value, balance is the sum of debit_credit values
      const sum = (arr: any[]) => arr.reduce((s, l) => s + (Number(l.debit_credit) || 0), 0);
      const depBal = sum(depreciationDetail);
      const dispBal = sum(disposalDetail);
      if (Math.abs(depBal) > 0.005 || Math.abs(dispBal) > 0.005) {
        console.warn('[FixedAssetsPostingService][buildDisposalPayload] Unbalanced entry', {depBal, dispBal});
      }
      if (!proceedsAcct || proceedsAcct === 'PROCEEDS-ACCOUNT-PENDING') {
        console.warn('[FixedAssetsPostingService][buildDisposalPayload] Proceeds account not configured. Using placeholder.');
      }
    } catch {
    }

    try {
      console.log('[FixedAssetsPostingService][buildDisposalPayload] asset id:', (asset as any)?.id, 'payload outline:', {
        AssetId: payload.AssetId, Amount: payload.Amount, Expenses: payload.Expenses,
        DisposalLines: payload.DisposalEntry?.detail?.length, DepLines: payload.DepreciationEntry?.detail?.length
      });
    } catch {
    }
    return payload;
  }

  /** Post asset disposal via backend */
  postAssetDisposal(asset: FixedAssetModel, cfg: FixedAssetConfigModel, disposal: {
    date: string;
    amount: number;
    expenses?: number
  }): Observable<{ entryId: string; amount: number }> {
    if (!asset?.id) {
      return throwError(() => new Error('Asset is required'));
    }
    if (!disposal?.date || disposal.amount == null) {
      return throwError(() => new Error('Disposal date and amount are required'));
    }

    // Build minimal intent payload per new API contract (API computes all GL detail)
    const toIsoMidnight = (iso: string) => {
      try {
        const d = new Date(iso);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}T00:00:00`;
      } catch {
        return iso;
      }
    };

    // Resolve org/user context
    let userId = '';
    try {
      const u = this.auth.currentUser();
      userId = String((u && (u.id || (u.userId ?? u.username))) || '');
    } catch {
    }

    const intent: any = {
      AssetId: String(asset.id),
      DisposalDate: toIsoMidnight(disposal.date),
      Amount: Number(disposal.amount),
      CompanyId: (asset as any)?.companyId || undefined,
      LocationId: (asset as any)?.locationId || undefined,
      EntityId: (asset as any)?.entityId || undefined,
      UserId: userId || undefined,
    };
    if (disposal.expenses != null && disposal.expenses !== undefined && disposal.expenses !== ('' as any)) {
      intent.Expenses = Number(disposal.expenses);
    }

    // Log the exact intent payload for diagnostics
    try {
      console.log('[FixedAssetsPostingService][postAssetDisposal] intent -> fixed-assets/posting/disposal', intent);
    } catch {
    }

    return this.api.post('fixed-assets/posting/disposal', intent).pipe(
      map((res: any) => ({entryId: String(res?.id || ''), amount: Number(disposal.amount || 0)}))
    );
  }
}
